import React, { useCallback, useMemo, useRef, useState } from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { CheckCircle, XCircle, ChevronRight, X } from "lucide-react";
import { FireMmkIcon } from "../../assets/icon";
import { CustomTypography } from "../custom-typography/custom-typography";
import { CustomMarkdown } from "../custom-markdown/custom-markdown";
import { QuizQuestion } from "../../types/mmk";
import { toMmkPublicLabel } from "../../helpers/mmkDisplayLabel";
import {
  mmkBackToMmkButtonStyles,
  mmkFocusSessionCounterStyles,
  mmkQuizCardStyles,
  getMmkQuizOptionStyles,
} from "./styles";
import { MmkPageShell } from "./mmk-page-shell";

interface QuizViewProps {
  question: QuizQuestion;
  questionIndex: number;
  totalQuestions: number;
  onAnswer: (
    itemId: string,
    correct: boolean,
    selectedOptionIndex: number,
  ) => void;
  onNext: () => void;
  onExit: () => void;
  initialRevealed?: boolean;
  initialSelectedOptionIndex?: number;
}

function shuffleWithIndex<T>(arr: T[]): { item: T; originalIndex: number }[] {
  const indexed = arr.map((item, i) => ({ item, originalIndex: i }));
  for (let i = indexed.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [indexed[i], indexed[j]] = [indexed[j], indexed[i]];
  }
  return indexed;
}

export const QuizView: React.FC<QuizViewProps> = ({
  question,
  questionIndex,
  totalQuestions,
  onAnswer,
  onNext,
  onExit,
  initialRevealed = false,
  initialSelectedOptionIndex,
}) => {
  const theme = useTheme();
  const mountedAtRef = useRef(Date.now());
  const shuffledOptions = useMemo(
    () => shuffleWithIndex(question.options),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- shuffle once per question
    [question.item_id],
  );
  const [selectedOriginalIndex, setSelectedOriginalIndex] = useState<
    number | null
  >(initialSelectedOptionIndex ?? null);
  const [revealed, setRevealed] = useState(initialRevealed);

  const handleOptionClick = useCallback(
    (e: React.MouseEvent, originalIndex: number) => {
      e.preventDefault();
      e.stopPropagation();
      if (revealed) return;
      if (Date.now() - mountedAtRef.current < 400) return;
      const option = question.options[originalIndex];
      const correct = option.correct;
      setSelectedOriginalIndex(originalIndex);
      setRevealed(true);
      onAnswer(question.item_id, correct, originalIndex);
    },
    [question, revealed, onAnswer],
  );

  const isDark = theme.palette.mode === "dark";
  const cardHeaderLabel = (
    question.card_header_label?.trim() ||
    toMmkPublicLabel(question.mkk_number ?? "")
  ).trim();

  return (
    <MmkPageShell>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          // Room for absolute Back button; footer padding comes from shell.
          pt: { xs: 6, sm: 7 },
          pb: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={onExit}
          sx={{
            ...mmkBackToMmkButtonStyles,
            position: "absolute",
            top: 0,
            right: 0,
          }}
          startIcon={<X size={18} />}
        >
          Back to MMM
        </Button>
        {/* my:auto centers when short; grows naturally so the shell can scroll */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            my: "auto",
          }}
        >
          <Box sx={mmkQuizCardStyles}>
            {cardHeaderLabel && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mb: 1.5,
                }}
              >
                <FireMmkIcon style={{ width: 16, height: 16 }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                    fontSize: "13px",
                  }}
                >
                  {cardHeaderLabel}
                </Typography>
              </Box>
            )}
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                mb: 1.5,
                display: "block",
              }}
            >
              {question.category}
            </Typography>
            <CustomTypography
              fontWeight={600}
              sx={{
                fontSize: { xs: "16px", sm: "18px" },
                color: theme.palette.text.primary,
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              {question.question}
            </CustomTypography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {shuffledOptions.map(({ item: opt, originalIndex }) => {
                const isSelected = selectedOriginalIndex === originalIndex;
                const showCorrect = revealed && opt.correct;
                const showWrong = revealed && isSelected && !opt.correct;
                const state: "neutral" | "correct" | "wrong" | "revealed" =
                  showCorrect
                    ? "correct"
                    : showWrong
                      ? "wrong"
                      : revealed
                        ? "revealed"
                        : "neutral";
                return (
                  <Button
                    key={originalIndex}
                    type="button"
                    variant="outlined"
                    fullWidth
                    disabled={revealed}
                    onClick={(e) => handleOptionClick(e, originalIndex)}
                    sx={getMmkQuizOptionStyles(state)}
                    startIcon={
                      revealed ? (
                        opt.correct ? (
                          <CheckCircle size={18} color="#22C55E" />
                        ) : showWrong ? (
                          <XCircle size={18} color="#EF4444" />
                        ) : null
                      ) : null
                    }
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "14px", sm: "15px" },
                        fontWeight: 500,
                        textAlign: "left",
                        width: "100%",
                      }}
                    >
                      {opt.text}
                    </Typography>
                  </Button>
                );
              })}
            </Box>
            {revealed && (
              <>
                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: isDark ? "rgba(34, 197, 94, 0.12)" : "#D1FAE5",
                    border: `1px solid ${isDark ? "rgba(34, 197, 94, 0.3)" : "#A7F3D0"}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    Correct answer
                  </Typography>
                  <Box
                    sx={{
                      fontSize: "15px",
                      fontWeight: 500,
                      color: isDark ? "#6EE7B7" : "#059669",
                      "& strong, & b": { fontWeight: 700 },
                      "& p": { m: 0, mb: 0.75, "&:last-child": { mb: 0 } },
                      "& ul, & ol": { m: 0, pl: 2.25 },
                      "& li": { mb: 0.5 },
                    }}
                  >
                    <CustomMarkdown skipHtml>
                      {question.approved_answer}
                    </CustomMarkdown>
                  </Box>
                </Box>
                <Button
                  type="button"
                  variant="contained"
                  fullWidth
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onNext();
                  }}
                  endIcon={<ChevronRight size={18} />}
                  sx={{
                    mt: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "12px",
                    py: 1.25,
                    bgcolor: "#14b8a6",
                    "&:hover": { bgcolor: "#0d9488" },
                  }}
                >
                  {questionIndex < totalQuestions - 1
                    ? "Next question"
                    : "See results"}
                </Button>
              </>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: 2,
            }}
          >
            <Typography sx={mmkFocusSessionCounterStyles}>
              {questionIndex + 1} / {totalQuestions}
            </Typography>
          </Box>
        </Box>
      </Box>
    </MmkPageShell>
  );
};
